// client/src/components/pages/Geofencing/GeofenceConfigFields.tsx (converted from .js, ST-00228)
// The LexiStar versioned-configuration sections of the zone editor (M1-C):
// purpose type, loading/offloading roles, rules with thresholds, entry/exit
// debounce, schedule and validity. Rendered by GeofenceForm only when the
// server reports `versioning` (migration 021 applied). Controlled: `value` is
// the versioned form state from ./geofenceConfig, `onChange(next)` replaces it.
//
// ST-00228 (audit TRK-8) / ST-00269: the Rules section says who applies the rules. Since the
// legacy geofence evaluator was retired (ST-00269) that is the LexiStar engine, for every
// rule, the entry and exit detection, the operating hours and the validity dates.
//
// ST-00282: the Alerts section (when the server has migration 072, `alertSwitch`): the zone's
// Alerts switch says WHETHER the zone alerts; the rules say WHICH crossings and rule breaks alert
// while it is on. Off keeps the rules (greyed: they apply again when alerts are switched on) and
// records everything without alerting. A no-go zone's switch is locked on with the reason. A new
// zone starts off; switching on a zone with no rule ticks On entry and On exit.
import type { ComponentType, ReactNode } from 'react';
import ThemedSelect from '../../common/ThemedSelect';
import ThemedTimePicker from '../../common/ThemedTimePicker';
import UnifiedDateRangePicker from '../../common/UnifiedDateRangePicker';
import {
  PURPOSE_TYPES as PURPOSE_TYPES_JS,
  PurposeSwatch as PurposeSwatchJs,
  getPurposeMeta as getPurposeMetaJs,
  defaultRolesFor as defaultRolesForJs,
} from './purposeTypes';
import {
  AFTER_HOURS_DIRECTIONS as AFTER_HOURS_DIRECTIONS_JS,
  RULE_KINDS as RULE_KINDS_JS,
  SEVERITIES as SEVERITIES_JS,
  WEEKDAYS as WEEKDAYS_JS,
  DEFAULT_WINDOW as DEFAULT_WINDOW_JS,
  NO_GO_PURPOSE,
  defaultSeverity as defaultSeverityJs,
  nogoAlertsReason,
  zoneAlertsLocked,
} from './geofenceConfig';
import ZoneAlertsSwitch from './ZoneAlertsSwitch';

/** One schedule window: ISO weekdays (1 = Monday) and HH:MM times. */
export interface ScheduleWindow {
  days: number[];
  from: string;
  to: string;
}

/** One rules-editor row (geofenceConfig rulesToForm). */
export interface RuleFormRow {
  enabled: boolean;
  severity: string;
  /** Threshold as typed (minutes / km/h), for rules that have one. */
  value?: string;
  /** after_hours: 'entry' | 'exit' | 'both'. */
  direction?: string;
}

/** The versioned form state (geofenceConfig versionedFormFromGeofence). */
export interface GeofenceVersionedForm {
  purposeType: string;
  loadingRole: boolean;
  offloadingRole: boolean;
  /** ST-00282: the zone's Alerts switch (a no-go zone is always on). */
  alertsEnabled: boolean;
  rules: Record<string, RuleFormRow | undefined>;
  debounce: {
    entry_fixes: number | string;
    entry_seconds: number | string;
    exit_buffer_m: number | string;
  };
  scheduleEnabled: boolean;
  schedule: { tz: string; windows: ScheduleWindow[] };
  validityEnabled: boolean;
  validFrom: Date | null;
  validTo: Date | null;
  validityInitial?: { enabled: boolean; from: string | null; to: string | null };
}

interface RuleKind {
  on: string;
  label: string;
  severity: string;
  threshold?: { key: string; unit: string; scale: number; placeholder: string };
  direction?: boolean;
}

interface IdLabel {
  id: string;
  label: string;
}

// Typed views of the JavaScript modules this component uses.
const PURPOSE_TYPES = PURPOSE_TYPES_JS as unknown as IdLabel[];
const PurposeSwatch = PurposeSwatchJs as unknown as ComponentType<{ purpose: string }>;
const getPurposeMeta = getPurposeMetaJs as unknown as (id: string) => { roles?: boolean };
const defaultRolesFor = defaultRolesForJs as unknown as (purposeType: string) => {
  loading: boolean;
  offloading: boolean;
};
const AFTER_HOURS_DIRECTIONS = AFTER_HOURS_DIRECTIONS_JS as unknown as IdLabel[];
const RULE_KINDS = RULE_KINDS_JS as unknown as RuleKind[];
const SEVERITIES = SEVERITIES_JS as unknown as IdLabel[];
const WEEKDAYS = WEEKDAYS_JS as unknown as { id: number; short: string }[];
const DEFAULT_WINDOW = DEFAULT_WINDOW_JS as unknown as ScheduleWindow;
const defaultSeverity = defaultSeverityJs as unknown as (
  on: string,
  purposeType?: string
) => string;

/** A ThemedSelect / time picker change value as a string. */
const valueOf = (e: { target: { value: unknown } }): string => String(e.target.value ?? '');

const inputClass =
  'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500';
const checkboxClass = 'w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500';

function Section({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <fieldset className="border border-gray-200 rounded-lg p-4">
      <legend className="px-1 text-sm font-semibold text-gray-800">{title}</legend>
      {hint && <p className="text-xs text-gray-500 -mt-1 mb-3">{hint}</p>}
      {children}
    </fieldset>
  );
}

/** Purpose type select with the LS-09 §4 fill swatch on every option. */
export function PurposeSelect({
  value,
  onChange,
  id = 'geofence-purpose',
}: {
  value: string;
  onChange: (purposeType: string) => void;
  id?: string;
}) {
  return (
    <ThemedSelect
      id={id}
      value={value}
      onChange={(e) => onChange(valueOf(e))}
      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
      formatOptionLabel={(opt, meta) => (
        <span className="inline-flex items-center gap-2">
          <PurposeSwatch purpose={String(opt.value)} />
          <span>
            {opt.label}
            {/* ST-00282: say it where the type is chosen (the Alerts section says it once chosen). */}
            {String(opt.value) === NO_GO_PURPOSE && meta?.context === 'menu' && (
              <span className="block text-xs text-red-700">Always alerts</span>
            )}
          </span>
        </span>
      )}
    >
      {PURPOSE_TYPES.map((p) => (
        <option key={p.id} value={p.id}>
          {p.label}
        </option>
      ))}
    </ThemedSelect>
  );
}

/** Weekday chips + from/to times for one schedule window ({ days, from, to }). */
export function WindowEditor({
  window: w,
  onChange,
  label,
}: {
  window: ScheduleWindow | null | undefined;
  onChange: (w: ScheduleWindow) => void;
  label: string;
}) {
  const win = w || DEFAULT_WINDOW;
  const toggleDay = (d: number) => {
    const days = win.days.includes(d)
      ? win.days.filter((x) => x !== d)
      : [...win.days, d].sort((a, b) => a - b);
    onChange({ ...win, days });
  };
  return (
    <div className="flex flex-wrap items-center gap-3">
      <div className="flex flex-wrap gap-1" role="group" aria-label={`${label} days`}>
        {WEEKDAYS.map((d) => {
          const on = win.days.includes(d.id);
          return (
            <button
              key={d.id}
              type="button"
              aria-pressed={on}
              onClick={() => toggleDay(d.id)}
              className={`px-2 py-1 text-xs font-medium rounded-md border transition-colors ${
                on
                  ? 'bg-teal-500 border-teal-500 text-white'
                  : 'bg-surface border-gray-300 text-gray-600 hover:bg-gray-50'
              }`}
            >
              {d.short}
            </button>
          );
        })}
      </div>
      <div className="flex items-center gap-2 text-xs text-gray-600">
        <span>from</span>
        <ThemedTimePicker
          value={win.from}
          onChange={(e) => onChange({ ...win, from: e.target.value })}
        />
        <span>to</span>
        <ThemedTimePicker
          value={win.to}
          onChange={(e) => onChange({ ...win, to: e.target.value })}
        />
      </div>
    </div>
  );
}

/** One editor row per FR-GEO-004 rule kind: enable, threshold, severity. */
export function RulesEditor({
  rules,
  purposeType,
  onChange,
  scheduleEnabled = false,
  disabled = false,
}: {
  rules: GeofenceVersionedForm['rules'];
  purposeType: string;
  onChange: (rules: GeofenceVersionedForm['rules']) => void;
  scheduleEnabled?: boolean;
  /** ST-00282: the zone's alerts are off — the rules are kept but cannot be edited. */
  disabled?: boolean;
}) {
  const setRow = (on: string, patch: Partial<RuleFormRow>) => {
    const current: RuleFormRow = rules[on] || {
      enabled: false,
      severity: defaultSeverity(on, purposeType),
    };
    onChange({ ...rules, [on]: { ...current, ...patch } });
  };
  return (
    <div className="divide-y divide-gray-100">
      {RULE_KINDS.map((k) => {
        const row: RuleFormRow = rules[k.on] || {
          enabled: false,
          severity: defaultSeverity(k.on, purposeType),
        };
        const inputId = `rule-${k.on}`;
        return (
          <div key={k.on} className="py-2 flex flex-col gap-2">
            <div className="flex flex-wrap items-center gap-3">
              <label className="flex items-center gap-2 cursor-pointer min-w-[13rem]">
                <input
                  type="checkbox"
                  checked={row.enabled}
                  disabled={disabled}
                  onChange={(e) => setRow(k.on, { enabled: e.target.checked })}
                  className={checkboxClass}
                />
                <span className="text-sm text-gray-700">{k.label}</span>
              </label>
              {k.threshold && (
                <div className="flex items-center gap-1.5">
                  <input
                    id={inputId}
                    type="number"
                    min="0"
                    step="any"
                    aria-label={`${k.label} (${k.threshold.unit})`}
                    value={row.value ?? ''}
                    disabled={disabled || !row.enabled}
                    placeholder={k.threshold.placeholder}
                    onChange={(e) => setRow(k.on, { value: e.target.value })}
                    className="w-24 px-2 py-1 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500 disabled:bg-gray-50 disabled:text-gray-400"
                  />
                  <span className="text-xs text-gray-500">{k.threshold.unit}</span>
                </div>
              )}
              {k.direction && (
                <div className="w-36">
                  <ThemedSelect
                    value={row.direction || 'both'}
                    onChange={(e) => setRow(k.on, { direction: valueOf(e) })}
                    disabled={disabled || !row.enabled}
                    aria-label={`${k.label} direction`}
                    className="h-8 text-xs"
                  >
                    {AFTER_HOURS_DIRECTIONS.map((d) => (
                      <option key={d.id} value={d.id}>
                        {d.label}
                      </option>
                    ))}
                  </ThemedSelect>
                </div>
              )}
              <div className="w-32 ml-auto">
                <ThemedSelect
                  value={row.severity}
                  onChange={(e) => setRow(k.on, { severity: valueOf(e) })}
                  disabled={disabled || !row.enabled}
                  aria-label={`${k.label} severity`}
                  className="h-8 text-xs"
                >
                  {SEVERITIES.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.label}
                    </option>
                  ))}
                </ThemedSelect>
              </div>
            </div>
            {k.direction && row.enabled && (
              <p className={`pl-6 text-xs ${scheduleEnabled ? 'text-gray-500' : 'text-amber-700'}`}>
                {scheduleEnabled
                  ? 'Checked against the operating hours set below.'
                  : 'Needs operating hours — set them under Schedule and validity.'}
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
}

/**
 * All versioned sections. `value` = versioned form state; `onChange(next)`.
 */
export interface GeofenceConfigFieldsProps {
  value: GeofenceVersionedForm;
  onChange: (next: GeofenceVersionedForm) => void;
  /** ST-00282: the server has the zone Alerts switch (migration 072). */
  alertSwitch?: boolean;
}

/** ST-00282: switching alerts on for a zone without any rule ticks On entry and On exit. */
function rulesForAlertsOn(
  rules: GeofenceVersionedForm['rules'],
  purposeType: string
): GeofenceVersionedForm['rules'] {
  if (Object.values(rules).some((r) => r?.enabled)) return rules;
  const tick = (on: string) => ({
    ...(rules[on] || { severity: defaultSeverity(on, purposeType) }),
    enabled: true,
  });
  return { ...rules, entry: tick('entry'), exit: tick('exit') };
}

export default function GeofenceConfigFields({
  value,
  onChange,
  alertSwitch = false,
}: GeofenceConfigFieldsProps) {
  const set = (patch: Partial<GeofenceVersionedForm>) => onChange({ ...value, ...patch });
  const meta = getPurposeMeta(value.purposeType);
  const alertsLocked = zoneAlertsLocked(value.purposeType);
  const alertsOn = alertsLocked || !alertSwitch || value.alertsEnabled;
  // A no-go zone's reason names its hours when it has a schedule.
  const lockedReason = nogoAlertsReason(value.scheduleEnabled ? value.schedule : null);

  const handlePurpose = (purposeType: string) => {
    const roles = defaultRolesFor(purposeType);
    // Re-derive the no-go entry severity only if the user never changed it.
    const entry = value.rules.entry;
    const entrySeverity =
      entry && entry.severity === defaultSeverity('entry', value.purposeType)
        ? defaultSeverity('entry', purposeType)
        : (entry?.severity ?? defaultSeverity('entry', purposeType));
    onChange({
      ...value,
      purposeType,
      loadingRole: roles.loading,
      offloadingRole: roles.offloading,
      rules: entry ? { ...value.rules, entry: { ...entry, severity: entrySeverity } } : value.rules,
      // ST-00282: a no-go zone always alerts (the server enforces it too).
      ...(zoneAlertsLocked(purposeType) ? { alertsEnabled: true } : {}),
    });
  };

  const setAlerts = (on: boolean) =>
    set({
      alertsEnabled: on,
      ...(on ? { rules: rulesForAlertsOn(value.rules, value.purposeType) } : {}),
    });

  const setDebounce = (key: keyof GeofenceVersionedForm['debounce'], v: string) =>
    set({ debounce: { ...value.debounce, [key]: v } });
  const scheduleWindow = value.schedule.windows[0];

  return (
    <div className="space-y-4">
      <Section
        title="Purpose"
        hint="Sets what the zone means to LexiStar (status, events and reports)."
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
          <div>
            <label
              htmlFor="geofence-purpose"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Purpose type
            </label>
            <PurposeSelect value={value.purposeType} onChange={handlePurpose} />
          </div>
          {meta.roles && (
            <div>
              <span className="block text-sm font-medium text-gray-700 mb-1">Roles</span>
              <div className="flex flex-wrap gap-4 pt-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!value.loadingRole}
                    onChange={(e) => set({ loadingRole: e.target.checked })}
                    className={checkboxClass}
                  />
                  <span className="text-sm text-gray-700">Loading</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!value.offloadingRole}
                    onChange={(e) => set({ offloadingRole: e.target.checked })}
                    className={checkboxClass}
                  />
                  <span className="text-sm text-gray-700">Offloading</span>
                </label>
              </div>
              <p className="text-xs text-gray-400 mt-1">
                A dual-role site can be both; the truck's active trip decides which applies.
              </p>
            </div>
          )}
        </div>
      </Section>

      {alertSwitch && (
        <Section title="Alerts">
          <div className="flex flex-wrap items-center gap-3" data-testid="zone-alerts-section">
            <span className="text-sm font-medium text-gray-700">Alerts for this zone</span>
            <ZoneAlertsSwitch
              checked={alertsOn}
              locked={alertsLocked}
              lockedReason={lockedReason}
              onChange={setAlerts}
              label="Alerts for this zone"
            />
          </div>
          <p data-testid="zone-alerts-hint" className="text-xs text-gray-500 mt-2">
            {alertsLocked
              ? lockedReason
              : alertsOn
                ? 'On: the rules below say which crossings and rule breaks raise an alert (Pending Alerts, notifications, escalation), and a site dwell limit exceeded here alerts too.'
                : 'Off: everything this zone raises is still recorded (events, visits, Replay, reports and site dwell) and map colour rules still see the crossings, but none of it alerts: no Pending Alert, notification or escalation.'}
          </p>
        </Section>
      )}

      <Section
        title="Rules"
        hint="Each enabled rule raises a typed event with the chosen severity."
      >
        <p data-testid="rules-engine-hint" className="text-xs text-gray-500 -mt-1 mb-3">
          The LexiStar engine applies every rule, the entry and exit detection, the operating hours
          and the validity dates. While the zone's alerts are on, an event a rule raises is an alert:
          it shows under Pending Alerts until someone acknowledges it on the Events tab, and
          notification rules can send it on.
        </p>
        {!alertsOn && (
          <p data-testid="rules-off-hint" className="text-xs text-amber-700 -mt-1 mb-3">
            Alerts are off for this zone: these rules are kept and apply again when you switch
            alerts on.
          </p>
        )}
        <RulesEditor
          rules={value.rules}
          purposeType={value.purposeType}
          scheduleEnabled={value.scheduleEnabled}
          disabled={!alertsOn}
          onChange={(rules) => set({ rules })}
        />
      </Section>

      <Section
        title="Entry and exit detection"
        hint="Debounce: entry after N consecutive fixes inside or T seconds; exit once beyond the buffer."
      >
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label
              htmlFor="debounce-fixes"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Entry fixes
            </label>
            <input
              id="debounce-fixes"
              type="number"
              min="1"
              max="50"
              value={value.debounce.entry_fixes}
              onChange={(e) => setDebounce('entry_fixes', e.target.value)}
              className={inputClass}
            />
          </div>
          <div>
            <label
              htmlFor="debounce-seconds"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Entry seconds
            </label>
            <input
              id="debounce-seconds"
              type="number"
              min="0"
              max="3600"
              value={value.debounce.entry_seconds}
              onChange={(e) => setDebounce('entry_seconds', e.target.value)}
              className={inputClass}
            />
          </div>
          <div>
            <label
              htmlFor="debounce-buffer"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Exit buffer (m)
            </label>
            <input
              id="debounce-buffer"
              type="number"
              min="0"
              max="1000"
              value={value.debounce.exit_buffer_m}
              onChange={(e) => setDebounce('exit_buffer_m', e.target.value)}
              className={inputClass}
            />
          </div>
        </div>
      </Section>

      <Section
        title="Schedule and validity"
        hint="Operating hours: a no-go zone is enforced only inside them, and the operating-hours rule alerts outside them."
      >
        <div className="space-y-4">
          <div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={value.scheduleEnabled}
                onChange={(e) => set({ scheduleEnabled: e.target.checked })}
                className={checkboxClass}
              />
              <span className="text-sm text-gray-700">Operating hours</span>
            </label>
            {value.scheduleEnabled && (
              <div className="mt-2 pl-6">
                <WindowEditor
                  label="Operating hours"
                  window={scheduleWindow}
                  onChange={(w) =>
                    set({
                      schedule: {
                        ...value.schedule,
                        windows: [w, ...value.schedule.windows.slice(1)],
                      },
                    })
                  }
                />
                {value.schedule.windows.length > 1 && (
                  <p className="text-xs text-gray-400 mt-1">
                    +{value.schedule.windows.length - 1} more window(s) set through the API are
                    kept.
                  </p>
                )}
              </div>
            )}
          </div>
          <div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={value.validityEnabled}
                onChange={(e) => {
                  const on = e.target.checked;
                  const today = new Date();
                  set({
                    validityEnabled: on,
                    validFrom: on ? value.validFrom || today : value.validFrom,
                    validTo: on
                      ? value.validTo || new Date(today.getTime() + 30 * 86400000)
                      : value.validTo,
                  });
                }}
                className={checkboxClass}
              />
              <span className="text-sm text-gray-700">Valid only between dates</span>
            </label>
            {value.validityEnabled && (
              <div className="mt-2 pl-6 max-w-sm">
                <UnifiedDateRangePicker
                  bounds="all"
                  startDate={value.validFrom}
                  endDate={value.validTo}
                  onStartDateChange={(d) => set({ validFrom: d })}
                  onEndDateChange={(d) => set({ validTo: d })}
                />
              </div>
            )}
          </div>
        </div>
      </Section>
    </div>
  );
}
